import {
	Box,
	Input,
	Portal,
	Show,
	Text,
	useToken,
	VStack,
} from '@chakra-ui/react';
import type React from 'react';
import { useRef, useState } from 'react';
import { BiCamera, BiDownload, BiTrash, BiUpload } from 'react-icons/bi';
import FaceliftAvatar from '@/components/Atoms/FaceliftAvatar';
import { MenuPrimitives as Menu } from '@/types/MenuPrimitives.types';
import CropPhotoDialog from './CropPhotoDialog';

interface ProfileCardProps {
	name: string;
	designation: string;
	company: string;
	avatarUrl?: string;
	onAvatarChange?: (file: File) => void;
	onRemovePhoto?: () => void;
	onDownloadPhoto?: () => void;
}

const ProfileCard: React.FC<ProfileCardProps> = ({
	name,
	designation,
	company,
	avatarUrl,
	onAvatarChange,
	onRemovePhoto,
	onDownloadPhoto,
}) => {
	const [brand600] = useToken('colors', ['brand.600']);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | undefined>(avatarUrl);
	const [isHovered, setIsHovered] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isCropDialogOpen, setIsCropDialogOpen] = useState(false);
	const [selectedImageUrl, setSelectedImageUrl] = useState<string>('');

	const handleAvatarClick = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const handleUploadPhoto = () => {
		setIsMenuOpen(false);
		fileInputRef.current?.click();
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (file) {
			const objectUrl = URL.createObjectURL(file);
			setSelectedImageUrl(objectUrl);
			setIsCropDialogOpen(true);
		}
	};

	const handleCropComplete = (croppedImageUrl: string) => {
		setPreviewUrl(croppedImageUrl);
		fetch(croppedImageUrl)
			.then((res) => res.blob())
			.then((blob) => {
				const file = new File([blob], 'cropped-avatar.jpg', {
					type: 'image/jpeg',
				});
				onAvatarChange?.(file);
			});
	};

	const handleCropCancel = () => {
		setIsCropDialogOpen(false);
		setSelectedImageUrl('');
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleRemovePhoto = () => {
		setIsMenuOpen(false);
		setPreviewUrl(undefined);
		onRemovePhoto?.();
	};

	const handleDownloadPhoto = () => {
		setIsMenuOpen(false);
		onDownloadPhoto?.();
	};

	return (
		<Box
			w={{ base: '100%', md: '305px' }}
			h="252px"
			rounded="md"
			textAlign="center"
			boxShadow={`0 0 10px ${brand600}80`}
		>
			<VStack align="center" justify="center" h="full" gap={5}>
				<Menu.Root
					open={isMenuOpen}
					onOpenChange={({ open }: { open: boolean }) => setIsMenuOpen(open)}
					positioning={{ placement: 'bottom-start' }}
				>
					<Menu.Trigger>
						<Box
							position="relative"
							rounded="full"
							overflow="hidden"
							w="92px"
							h="92px"
							cursor="pointer"
							onClick={handleAvatarClick}
							onMouseEnter={() => setIsHovered(true)}
							onMouseLeave={() => setIsHovered(false)}
						>
							<FaceliftAvatar name={name} previewUrl={previewUrl || ''} />
							<Show when={isHovered}>
								<Box
									position="absolute"
									top="0"
									left="0"
									right="0"
									bottom="0"
									bg="blackAlpha.600"
									display="flex"
									flexDirection="column"
									alignItems="center"
									justifyContent="center"
									color="white"
									fontSize="xs"
									fontWeight="medium"
									transition="all 0.2s"
								>
									<BiCamera size={20} />
									<Text
										fontSize="8px"
										mt={1}
										textAlign="center"
										lineHeight="1.2"
									>
										Change
										<br />
										profile picture
									</Text>
								</Box>
							</Show>
						</Box>
					</Menu.Trigger>

					<Portal>
						<Menu.Positioner>
							<Menu.Content bg="brand.pageBackground">
								<Menu.Item
									onClick={handleUploadPhoto}
									_hover={{
										bg: 'gray.100',
										color: 'gray.900',
										cursor: 'pointer',
									}}
									display="flex"
									alignItems="center"
									gap={2}
									height="39px"
								>
									<BiUpload />
									Upload
								</Menu.Item>
								<Menu.Item
									onClick={handleRemovePhoto}
									_hover={{ bg: 'red.50', color: 'red.600', cursor: 'pointer' }}
									display="flex"
									alignItems="center"
									gap={2}
									height="39px"
									disabled={!previewUrl}
								>
									<BiTrash />
									Remove
								</Menu.Item>
								<Menu.Item
									onClick={handleDownloadPhoto}
									_hover={{
										bg: 'blue.50',
										color: 'blue.600',
										cursor: 'pointer',
									}}
									display="flex"
									alignItems="center"
									gap={2}
									height="39px"
									disabled={!previewUrl}
								>
									<BiDownload />
									Download
								</Menu.Item>
							</Menu.Content>
						</Menu.Positioner>
					</Portal>
				</Menu.Root>

				<Input
					type="file"
					accept="image/*"
					display="none"
					ref={fileInputRef}
					onChange={handleFileChange}
				/>

				<Box>
					<Text fontSize="26px" fontWeight="bold">
						{name}
					</Text>
					<Text
						fontSize="12px"
						color={{ _dark: 'gray.400', _light: 'gray.900' }}
					>
						{designation} - {company}
					</Text>
				</Box>
			</VStack>

			<CropPhotoDialog
				isOpen={isCropDialogOpen}
				onClose={handleCropCancel}
				imageUrl={selectedImageUrl}
				onCropComplete={handleCropComplete}
			/>
		</Box>
	);
};

export default ProfileCard;
